import type { Map as MapboxMap } from "mapbox-gl"
import type { LightPreset } from "../map/camera.ts"

export type MapStyle = "terrain" | "satellite"

/**
 * Both basemaps are Mapbox Standard variants.
 *
 * They replaced two hand-tuned v2-era Studio styles, which were fine to look at
 * but frozen out of everything Mapbox GL 3 can do — `lightPreset`, dynamic
 * lighting, 3D objects. The Trip Player wanted exactly one of those (lighting
 * the scene for the hour the photo was taken), and the alternative was keeping
 * a style that could never have it.
 *
 * Standard's documented restrictions cost this app nothing: it never calls
 * `queryRenderedFeatures` (POIs are DOM markers, so clicks are DOM events), it
 * only ever adds and removes its *own* layers, and it never filters a basemap
 * layer. The one real obligation is `slot` — see `STANDARD_OVERLAY_SLOT`.
 *
 * Keeping both on Standard also means `setConfigProperty("basemap", …)` is
 * valid whichever is showing, so no call site needs a per-style guard.
 */
export const STYLE_URLS: Record<MapStyle, string> = {
  terrain: "mapbox://styles/mapbox/standard",
  satellite: "mapbox://styles/mapbox/standard-satellite",
}

export const STYLE_STORAGE_KEY = "trippin:map-style"

/**
 * Where the app's own layers sit in Standard's stack.
 *
 * Standard's basemap layers are internal, so there is no id to pass as
 * `beforeId` and an unslotted layer lands wherever the style decides — which
 * for a route line means under roads and labels. `top` puts the trip's own
 * marks above the basemap, which is where they belong: they are the subject.
 */
export const STANDARD_OVERLAY_SLOT = "top"

/** Mapbox raster DEM source definition used by both map components. */
export const DEM_SOURCE_ID = "mapbox-dem"
export const DEM_SOURCE_DEF = {
  type: "raster-dem" as const,
  url: "mapbox://mapbox.mapbox-terrain-dem-v1",
  tileSize: 512,
  maxzoom: 14,
}

/**
 * Standard style configuration the app sets, by the names Mapbox gives them.
 *
 * `basemap` is the import id every Standard style exposes its config under.
 */
export const BASEMAP_IMPORT_ID = "basemap"
export const CONFIG_LIGHT_PRESET = "lightPreset"
export const CONFIG_SHOW_POI_LABELS = "showPointOfInterestLabels"

// -----------------------------------------------------------------------------
// Depth: terrain, hillshade, fog
// -----------------------------------------------------------------------------

/**
 * Relief shading, laid over the same DEM the terrain mesh is built from.
 *
 * Terrain geometry alone gives a pitched view a silhouette and nothing else:
 * Standard's default `day` preset puts the sun overhead, so two slopes facing
 * opposite directions come back the same colour and the range reads flat. The
 * hillshade layer is what puts a light side and a dark side on every ridge, and
 * it costs one `addLayer` because `DEM_SOURCE_ID` is already on the map.
 *
 * `bottom` rather than `STANDARD_OVERLAY_SLOT` — this is ground, so it belongs
 * under Standard's roads and labels. The route line is the subject and stays on
 * `top`; shading that painted over labels would be a bug, not depth.
 */
export const HILLSHADE_LAYER_ID = "terrain-hillshade"
export const HILLSHADE_SLOT = "bottom"

/**
 * A second DEM source, holding the same tiles as `DEM_SOURCE_ID`.
 *
 * Not redundancy. Mapbox downsamples a raster-DEM source that is feeding the
 * terrain mesh *and* a hillshade layer at once, and says so at runtime: "used
 * both for terrain and as layer source. This leads to lower resolution of
 * hillshade." Low-resolution relief is the exact thing this layer exists to
 * fix, so the shading gets a source of its own.
 *
 * The cost is a second set of DEM tiles in memory. If that ever needs winning
 * back, point the layer at `DEM_SOURCE_ID` and the warning — and the softer
 * shading — both come back.
 */
export const HILLSHADE_SOURCE_ID = "mapbox-dem-hillshade"

/**
 * How hard to shade, per basemap.
 *
 * Satellite gets less than half of terrain's. Satellite imagery is photographs
 * taken in real sunlight, so the hills already carry their own shadows; shading
 * it as hard as the flat vector basemap double-darkens every north face.
 */
export const HILLSHADE_EXAGGERATION: Record<MapStyle, number> = {
  terrain: 0.5,
  satellite: 0.25,
}

/**
 * Terrain mesh exaggeration, and the styles that get a mesh at all.
 *
 * Both of them do. Satellite used to be excluded — `setTerrain(null)` whenever
 * the style was not `terrain` — which made the one basemap showing real
 * photographs of real mountains the one basemap that drew them flat. Nothing
 * about the mesh is style-specific: it is the same DEM, at the same
 * exaggeration, and imagery drapes over it exactly as vector fills do.
 *
 * Hillshade is the part that still differs, and deliberately — see
 * `HILLSHADE_EXAGGERATION`.
 */
export const TERRAIN_EXAGGERATION = 1.5
export const STYLES_WITH_TERRAIN: readonly MapStyle[] = ["terrain", "satellite"]

/**
 * The atmosphere and shading colours that go with one `lightPreset`.
 *
 * Fog is the other half of depth, and the more valuable half on a wide pitched
 * view: distant ridges losing contrast into haze is the cue that reads as
 * *far*, and no amount of terrain exaggeration substitutes for it.
 *
 * It has to be a table rather than one constant because `setFog` **replaces**
 * Standard's own fog outright, and Standard's is tuned per preset. A single
 * daylight-blue haze left standing through a `night` step — which the Trip
 * Player asks for whenever a photo was taken after dark — would light the
 * horizon like noon under a black sky. Hillshade tints ride along in the same
 * table for the same reason: white highlights at midnight read as snow.
 */
export interface DepthPalette {
  fog: {
    range: [number, number]
    "horizon-blend": number
    color: string
    "high-color": string
    "space-color": string
    "star-intensity": number
  }
  hillshadeShadow: string
  hillshadeHighlight: string
}

export const DEPTH_PALETTES: Record<LightPreset, DepthPalette> = {
  dawn: {
    fog: {
      range: [1, 12],
      "horizon-blend": 0.15,
      color: "#f3ddc8",
      "high-color": "#a9b7d8",
      "space-color": "#1b2140",
      "star-intensity": 0.05,
    },
    hillshadeShadow: "#3a3348",
    hillshadeHighlight: "#ffe9d0",
  },
  day: {
    fog: {
      range: [1, 12],
      "horizon-blend": 0.1,
      color: "#dfe9f5",
      "high-color": "#8fb8e8",
      "space-color": "#0b1026",
      "star-intensity": 0,
    },
    hillshadeShadow: "#2b2b35",
    hillshadeHighlight: "#ffffff",
  },
  dusk: {
    fog: {
      range: [1, 12],
      "horizon-blend": 0.15,
      color: "#e9c7ad",
      "high-color": "#7d8fc0",
      "space-color": "#161a33",
      "star-intensity": 0.1,
    },
    hillshadeShadow: "#332b3d",
    hillshadeHighlight: "#ffdcb8",
  },
  night: {
    fog: {
      range: [1, 12],
      "horizon-blend": 0.05,
      color: "#12182b",
      "high-color": "#2a3557",
      "space-color": "#05070f",
      "star-intensity": 0.5,
    },
    hillshadeShadow: "#070a14",
    hillshadeHighlight: "#5b6a92",
  },
}

/**
 * Repaint the atmosphere and the relief tints for one moment of the day.
 *
 * Split out from `applyDepth` because `setLight` runs on every player step and
 * has no reason to re-add a source or a layer that is already there. Silent
 * when the hillshade layer is missing — the caller may be on a style that never
 * got one, and a flourish must never take a flight down with it.
 *
 * Guarded by a read of the live fog, the same way `TripMap._applyConfig` guards
 * its config writes, and for the same reason. `setFog` and `setPaintProperty`
 * schedule a repaint unconditionally; measured, each costs about 40ms — a
 * dropped frame. That is nothing once, but the panel's scroll-sync focuses a
 * POI on every scroll tick, and two dropped frames per tick is a stutter the
 * viewer feels while dragging.
 *
 * Read against the map rather than a cache of what was last written, because
 * `setStyle` clears fog silently: a write-cache would believe the palette still
 * stood and skip restoring it after a basemap switch. Reading the live value is
 * self-healing — the colour comes back different, so the palette re-applies
 * exactly once and then goes quiet. See
 * `wiki/findings/mapbox-config-repaint-loop.md` for the general rule.
 */
export function applyLightPalette(
  map: MapboxMap,
  light: LightPreset,
): void {
  const palette = DEPTH_PALETTES[light] ?? DEPTH_PALETTES.day
  try {
    // Each preset's fog colour is distinct, so the colour alone identifies the
    // palette standing on the map.
    if (map.getFog()?.color === palette.fog.color) return
    map.setFog(palette.fog)
    if (map.getLayer(HILLSHADE_LAYER_ID)) {
      map.setPaintProperty(
        HILLSHADE_LAYER_ID,
        "hillshade-shadow-color",
        palette.hillshadeShadow,
      )
      map.setPaintProperty(
        HILLSHADE_LAYER_ID,
        "hillshade-highlight-color",
        palette.hillshadeHighlight,
      )
    }
  } catch {
    // Style mid-load. The next `applyDepth` or `setLight` will land it.
  }
}

/**
 * Put the whole depth stack on a map: DEM source, terrain, hillshade, fog.
 *
 * Idempotent, and written to be called again after every `setStyle` — that call
 * clears sources, layers, terrain and fog alike, so the three call sites that
 * used to re-add the DEM by hand all route here instead.
 */
export function applyDepth(
  map: MapboxMap,
  style: MapStyle,
  light: LightPreset = "day",
): void {
  try {
    if (!map.getSource(DEM_SOURCE_ID)) {
      map.addSource(DEM_SOURCE_ID, DEM_SOURCE_DEF)
    }
    if (!map.getSource(HILLSHADE_SOURCE_ID)) {
      map.addSource(HILLSHADE_SOURCE_ID, DEM_SOURCE_DEF)
    }

    map.setTerrain(
      STYLES_WITH_TERRAIN.includes(style)
        ? { source: DEM_SOURCE_ID, exaggeration: TERRAIN_EXAGGERATION }
        : null,
    )

    const exaggeration = HILLSHADE_EXAGGERATION[style]
    if (map.getLayer(HILLSHADE_LAYER_ID)) {
      map.setPaintProperty(
        HILLSHADE_LAYER_ID,
        "hillshade-exaggeration",
        exaggeration,
      )
    } else {
      map.addLayer({
        id: HILLSHADE_LAYER_ID,
        type: "hillshade",
        source: HILLSHADE_SOURCE_ID,
        slot: HILLSHADE_SLOT,
        paint: {
          "hillshade-exaggeration": exaggeration,
          // `map` rather than `viewport`: the light stays put as the camera
          // orbits, so a ridge keeps its lit face while the player drifts
          // around it. Anchored to the viewport it would swing with the
          // bearing and the terrain would appear to breathe.
          "hillshade-illumination-anchor": "map",
        },
      })
    }

    applyLightPalette(map, light)
  } catch {
    // Style mid-load, or a style with no room for these. Depth is a flourish;
    // never let it take the map down with it.
  }
}
